import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';

import { OperationProfile, OperationProfileRequest } from '../models/operation-profile.model';
import { WizardModal } from '../../../shared/components/wizard-modal/wizard-modal';
import { WizardStep } from '../../../shared/components/wizard-modal/wizard-step';
import { Icon } from '../../../shared/components/icon/icon';
import { IconName } from '../../../shared/components/icon/icon-name';
import {
  THRESHOLD_PRESETS,
  ThresholdEditor,
  ThresholdValues,
  matchThresholdPreset,
} from './threshold-editor/threshold-editor';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Toggle } from '../../../shared/components/forms/toggle';

export type DialogMode = 'add' | 'edit';

type PowerMode = 'MAIN' | 'BATTERY';

/** Power-saving bands must be ordered: exit above enter, no-power band below
 *  the low-power band, and the bands must not overlap (no-power exit at or
 *  below low-power enter). Keys map to profileWizard.errors.*. */
const thresholdOrder: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const v = group.value;
  const errors: ValidationErrors = {};
  if (v.noPowerPercentage != null && v.lowPowerPercentage != null) {
    if (v.noPowerPercentage >= v.lowPowerPercentage) errors['bandOrder'] = true;
  }
  if (v.noPowerPercentageOut != null && v.lowPowerPercentage != null) {
    if (v.noPowerPercentageOut > v.lowPowerPercentage) errors['overlapOrder'] = true;
  }
  if (v.lowPowerPercentage != null && v.lowPowerPercentageOut != null) {
    if (v.lowPowerPercentageOut <= v.lowPowerPercentage) errors['lowOrder'] = true;
  }
  if (v.noPowerPercentage != null && v.noPowerPercentageOut != null) {
    if (v.noPowerPercentageOut <= v.noPowerPercentage) errors['noOrder'] = true;
  }
  return Object.keys(errors).length ? errors : null;
};

/** New profiles start on the product-blessed 'recommended' preset — one source
 *  of truth with the editor's preset chips and the review's preset label. */
const THRESHOLD_DEFAULTS: ThresholdValues = THRESHOLD_PRESETS.find(
  (p) => p.key === 'recommended',
)!.values;

const TIMING_DEFAULTS = {
  renderInterval: 3600,
  connectionInterval: 86400,
  allowWakeup: true,
};

/** Review-step display unit for an interval in seconds. */
interface IntervalView {
  unit: 'd' | 'h' | 'min' | 's';
  n: number;
}

/**
 * Operation-profile create/edit wizard (the first real `app-wizard-modal`
 * consumer, built from the "Operation Profile Wizard" design). Steps: Basics
 * (ID + power mode), Power thresholds (battery only), Timing, Review.
 *
 * The API requires threshold/interval values for mains profiles too, so the
 * hidden battery-only step still submits its (defaulted or loaded) values.
 * External contract is unchanged: mount under @if, [profile]/[mode] in,
 * (save)/(close) out — the parent persists and closes on success.
 */
@Component({
  selector: 'app-profile-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    WizardModal,
    WizardStep,
    Icon,
    ThresholdEditor,
    FormField,
    TextInput,
    NumberInput,
    Toggle,
  ],
  templateUrl: './profile-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileDialogComponent {
  private readonly fb = inject(NonNullableFormBuilder);

  readonly profile = input<OperationProfile | null>(null);
  readonly mode = input<DialogMode>('add');
  /** True while the parent's save request is in flight; freezes the wizard. */
  readonly busy = input(false);
  readonly close = output<void>();
  readonly save = output<OperationProfileRequest>();

  /** Raw stored powerMode ('battery', future modes, ...) — round-tripped
   *  unchanged unless the user actually picks a mode. */
  private loadedPowerMode: string | null = null;
  /** Threshold values to submit when the thresholds step is hidden (MAIN):
   *  the loaded profile's stored values, or the defaults for a new profile —
   *  never abandoned battery-mode experiments from the hidden form. */
  private thresholdBaseline: ThresholdValues = { ...THRESHOLD_DEFAULTS };

  /** Drives the wizard shell's two-way `open`; closing routes through onClose(). */
  readonly open = signal(true);

  readonly basicsForm = this.fb.group({
    operationProfileId: ['', Validators.required],
    powerMode: this.fb.control<PowerMode>('MAIN'),
  });

  readonly thresholdsForm = this.fb.group(
    {
      lowPowerPercentage: [
        THRESHOLD_DEFAULTS.lowPowerPercentage,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      lowPowerPercentageOut: [
        THRESHOLD_DEFAULTS.lowPowerPercentageOut,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      noPowerPercentage: [
        THRESHOLD_DEFAULTS.noPowerPercentage,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      noPowerPercentageOut: [
        THRESHOLD_DEFAULTS.noPowerPercentageOut,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
    },
    { validators: thresholdOrder },
  );

  readonly timingForm = this.fb.group({
    renderInterval: [TIMING_DEFAULTS.renderInterval, [Validators.required, Validators.min(0)]],
    connectionInterval: [
      TIMING_DEFAULTS.connectionInterval,
      [Validators.required, Validators.min(0)],
    ],
    allowWakeup: [TIMING_DEFAULTS.allowWakeup],
  });

  /** Drives the battery-only thresholds step in the template. */
  protected readonly powerMode = toSignal(this.basicsForm.controls.powerMode.valueChanges, {
    initialValue: this.basicsForm.controls.powerMode.value,
  });

  /** Radio-card options; i18n keys derive from `key` (fields.main/mainDesc…). */
  protected readonly powerModes: readonly { value: PowerMode; key: string; icon: IconName }[] = [
    { value: 'MAIN', key: 'main', icon: 'icon-plug' },
    { value: 'BATTERY', key: 'battery', icon: 'icon-battery' },
  ];

  constructor() {
    // Patch/reset the forms whenever the bound profile or mode changes.
    effect(() => {
      const profile = this.profile();
      const mode = this.mode();
      untracked(() => this.applyProfile(profile, mode));
    });
  }

  private applyProfile(profile: OperationProfile | null, mode: DialogMode): void {
    if (profile && mode === 'edit') {
      this.loadedPowerMode = profile.powerMode;
      this.thresholdBaseline = {
        lowPowerPercentage: profile.lowPowerPercentage,
        lowPowerPercentageOut: profile.lowPowerPercentageOut,
        noPowerPercentage: profile.noPowerPercentage,
        noPowerPercentageOut: profile.noPowerPercentageOut,
      };
      this.basicsForm.patchValue({
        operationProfileId: profile.operationProfileId,
        // Case-insensitive for the UI; the raw value is preserved on save
        // unless the user actually picks a mode (see onFinished).
        powerMode: profile.powerMode?.toUpperCase() === 'BATTERY' ? 'BATTERY' : 'MAIN',
      });
      this.thresholdsForm.patchValue(profile);
      this.timingForm.patchValue(profile);
      // The profile id is the immutable business key.
      this.basicsForm.controls.operationProfileId.disable();
    } else {
      this.loadedPowerMode = null;
      this.thresholdBaseline = { ...THRESHOLD_DEFAULTS };
      this.basicsForm.reset({ operationProfileId: '', powerMode: 'MAIN' });
      this.thresholdsForm.reset({ ...THRESHOLD_DEFAULTS });
      this.timingForm.reset({ ...TIMING_DEFAULTS });
      this.basicsForm.controls.operationProfileId.enable();
    }
  }

  protected setPowerMode(mode: PowerMode): void {
    const control = this.basicsForm.controls.powerMode;
    // Re-selecting the active mode is a no-op; don't dirty the form (a dirty
    // form makes closing require the discard confirmation).
    if (control.value === mode) return;
    control.setValue(mode);
    control.markAsDirty();
  }

  protected onClose(): void {
    this.close.emit();
  }

  /** Last-step finish: all present steps are valid; the parent persists. */
  protected onFinished(): void {
    const basics = this.basicsForm.getRawValue();
    const isBattery = basics.powerMode === 'BATTERY';
    this.save.emit({
      operationProfileId: basics.operationProfileId,
      // Round-trip the stored value (casing, unknown modes) when untouched.
      powerMode: this.basicsForm.controls.powerMode.dirty
        ? basics.powerMode
        : (this.loadedPowerMode ?? basics.powerMode),
      // The thresholds step only exists for battery profiles; for mains,
      // submit the untouched baseline, never the hidden form's leftovers.
      ...(isBattery ? this.thresholdsForm.getRawValue() : this.thresholdBaseline),
      ...this.timingForm.getRawValue(),
    });
  }

  /** Preset key the current thresholds match, or null for custom values. */
  protected thresholdPresetKey(): string | null {
    return matchThresholdPreset(this.thresholdsForm.getRawValue())?.key ?? null;
  }

  /** Key of the message for the thresholds step banner: cross-field ordering
   *  errors first, then per-control range errors (reachable only by patching
   *  out-of-contract stored data; the editor clamps interactive input). */
  protected thresholdErrorKey(): string | null {
    const errors = this.thresholdsForm.errors;
    if (errors) {
      if (errors['bandOrder']) return 'bandOrder';
      if (errors['overlapOrder']) return 'overlapOrder';
      if (errors['lowOrder']) return 'lowOrder';
      return 'noOrder';
    }
    const rangeInvalid = Object.values(this.thresholdsForm.controls).some((c) => c.invalid);
    return rangeInvalid ? 'percent' : null;
  }

  /** Humanized interval for the review step (86400 s reads as 1 d). */
  protected interval(seconds: number | null): IntervalView {
    const v = seconds ?? 0;
    if (v >= 86400 && v % 86400 === 0) return { unit: 'd', n: v / 86400 };
    if (v >= 3600 && v % 3600 === 0) return { unit: 'h', n: v / 3600 };
    if (v >= 60 && v % 60 === 0) return { unit: 'min', n: v / 60 };
    return { unit: 's', n: v };
  }

  // ── Template validation helpers ──────────────────────────────────────

  protected isInvalid(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  protected err(control: AbstractControl, message: string): string | null {
    return this.isInvalid(control) ? message : null;
  }
}
